import React, { useState, useRef, useCallback, useEffect } from 'react';

import { useCurrentOrganization } from 'src/store/organization';
import { apiGetAllOrganizationTags, apiCreateOrganizationTag } from 'src/utils/journeyApi';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { MultiSelectItem, MultiSelectWithSearch } from 'src/common/multi-select/with-search';
import { functionNoop } from 'src/utils/function/noop';
import { Button } from 'src/common/button';
import { AddStrongIcon, CloseStrongIcon } from 'src/monet/icons';
import { IconButton } from 'src/common/icon-button';
import { TextInput } from 'src/common/form/text-input';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { Checkbox } from 'src/common/form/checkbox';
import { useCurrentTags } from 'src/store/tags';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface TagSelectionButtonProps {
  onTagsSelected: (tags: { id: number; name: string }[]) => void;
}

export const TagSelectionButton: React.FC<TagSelectionButtonProps> = ({ onTagsSelected }) => {
  const { currentOrganization } = useCurrentOrganization();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [selectedTags, setSelectedTags] = useState<MultiSelectItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newTagInput, setNewTagInput] = useState<string | null>(null);
  const [listedTags, setListedTags] = useState<MultiSelectItem[]>([]);
  const [fullTagsList, setFullTagsList] = useState<MultiSelectItem[]>([]);
  const { setCurrentTags } = useCurrentTags();
  const { checkFeature, showUpgradeModal } = useFeatureRestrictions();

  const openRef = useRef(functionNoop);
  const closeRef = useRef(functionNoop);

  const fetchTags = useCallback(async () => {
    const tagsResponse = await apiGetAllOrganizationTags(currentOrganization.id);
    const formattedTags = tagsResponse?.tags?.map((item: { id: number; name: string }) => ({
      value: String(item.id),
      label: item.name,
    }));
    setCurrentTags(tagsResponse?.tags);
    setFullTagsList(formattedTags);
    setListedTags(formattedTags);
  }, [currentOrganization.id]);

  const handleOpen = useCallback(() => {
    fetchTags();
    setIsOpen(true);
  }, [fetchTags]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleItemSelect = async (item: MultiSelectItem) => {
    const updatedTags = [...selectedTags, item];
    setSelectedTags(updatedTags);
    onTagsSelected(updatedTags.map((tag) => ({ id: parseInt(tag.value), name: tag.label })));
  };

  const handleItemDeselect = async (item: MultiSelectItem) => {
    const updatedTags = selectedTags.filter((tag) => tag.value !== item.value);
    setSelectedTags(updatedTags);
    onTagsSelected(updatedTags.map((tag) => ({ id: parseInt(tag.value), name: tag.label })));
  };

  const handleItemAdd = async (item: MultiSelectItem) => {
    // Check if user can create tags (Grow and Scale plans)
    if (!checkFeature('tags_and_filters')) {
      showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to create custom tags');
      return;
    }
    
    try {
      const { tag } = await apiCreateOrganizationTag(currentOrganization.id, item.label);
      const newTag = { value: String(tag.id), label: tag.name };
      handleItemSelect(newTag);
      fetchTags();
      setSuccessNotification('Tag created successfully');
    } catch (error) {
      setErrorNotification('Something went wrong creating tag');
    }
  };

  const toggleOpen = () => {
    if (isOpen) {
      closeRef.current();
    } else {
      openRef.current();
    }
  };
  const saveNewTag = async () => {
    if (newTagInput?.length && newTagInput?.length > 0 && currentOrganization?.id) {
          // Check if user can create tags (Grow and Scale plans)
    if (!checkFeature('tags_and_filters')) {
      showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to create custom tags');
      return;
    }
      
      await apiCreateOrganizationTag(currentOrganization.id, newTagInput)
        .then(() => {
          fetchTags();
          setSuccessNotification('Tag created successfully');
        })
        .catch(() => {
          setErrorNotification('Something went wrong creating tag');
        });
    }
    setNewTagInput('');
  };

  const onSearchInputChanged = (searchInput: string) => {
    if (!searchInput) {
      setListedTags([...fullTagsList]);
      return;
    }
    setListedTags(fullTagsList.filter((tag) => tag.label.toLowerCase().includes(searchInput.toLowerCase())));
  };
  return (
    <div>
      <Button
        className='self-start'
        label='Add Tag'
        size='regular'
        variant='secondary'
        icon={<AddStrongIcon />}
        onClick={toggleOpen}
      />
      <MultiSelectWithSearch
        name='content-tags'
        searchPlaceholder='Search for tags'
        items={listedTags}
        selectedItems={selectedTags}
        placement='bottom-start'
        openRef={openRef}
        closeRef={closeRef}
        onOpen={handleOpen}
        onClose={handleClose}
        onItemSelect={handleItemSelect}
        onItemDeselect={handleItemDeselect}
        onItemAdd={handleItemAdd}
        onSearchInputChange={onSearchInputChanged}
        renderNotFound={() => <span>No results found</span>}
        openAfterMount={false}
        renderOrigin={() => <></>}
        renderListItem={(item, onClick) => {
          const isItemSelected = selectedTags.some((option) => option?.value === item.value);
          return (
            <div className='flex items-center hover:opacity-80' onClick={onClick}>
              <Checkbox checked={isItemSelected} isBedrock label={item.label} />
            </div>
          );
        }}
        renderSelectedList={({ onItemClick, onRemoveClick }) => (
          <div className='flex flex-wrap gap-2 mt-2'>
            {selectedTags.map((tag) => (
              <span key={tag.value} className='bg-gray-200 px-2 py-1 rounded-full text-sm flex items-center'>
                {tag.label}
                <IconButton onClick={onRemoveClick(tag)}>
                  <CloseStrongIcon />
                </IconButton>
              </span>
            ))}
          </div>
        )}
        renderFooter={() => (
          <>
            {checkFeature('tags_and_filters') ? (
              <TextInput
                id={`new-tag`}
                onChange={(e: any) => {
                  e.preventDefault();
                  setNewTagInput(e.target.value);
                }}
                value={newTagInput || ''}
                hideOptional
                trailingAddon={
                  <IconButton onClick={saveNewTag}>
                    <ArrowRightIcon className='w-5 h-5' />
                  </IconButton>
                }
                inputProps={{
                  type: 'text',
                  placeholder: 'New Tag',
                }}
              />
            ) : (
              <div className="p-3 text-center text-sm text-gray-500">
                <p>Tag creation requires Scale plan</p>
                <button 
                  onClick={() => showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to create custom tags')}
                  className="text-blue-600 hover:text-blue-800 underline mt-1"
                >
                  Upgrade now
                </button>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};
